import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const navigation = [
  { name: 'Forside', href: '/' },
  { name: 'Økonomisk rådgivning', href: '/oekonomisk-raadgivning' },
  { name: 'Juridisk rådgivning', href: '/juridisk-raadgivning' },
  { name: 'Interim', href: '/interim-opgaver' },
  { name: 'Full service', href: '/full-service' },
  { name: 'Om os', href: '/om-finanju' },
  { name: 'Kontakt', href: '/kontakt' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const isActive = (path) => location.pathname === path;

  const menuVariants = {
    closed: {
      x: '100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    },
    opened: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    closed: { opacity: 0, x: 20 },
    opened: { opacity: 1, x: 0 },
  };

  const overlayVariants = {
    closed: { opacity: 0 },
    opened: { opacity: 1 },
  };

  return (
    <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Global">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <div className="flex lg:flex-1">
            <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-3">
              <img src="/logo.png" alt="FINANJU" className="h-10 w-auto" />
            </Link>
          </div>
          
          {/* Mobile menu button (Animated Burger) */}
          <div className="flex lg:hidden">
            <button
              type="button"
              className="relative z-[60] -m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-slate-900 focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">{mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}</span>
              <div className="relative flex flex-col items-center justify-center w-6 h-6">
                <motion.span
                  animate={mobileMenuOpen ? { rotate: 45, y: 0 } : { rotate: 0, y: -6 }}
                  className="absolute block h-0.5 w-6 bg-slate-900 rounded-full"
                />
                <motion.span
                  animate={mobileMenuOpen ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
                  className="absolute block h-0.5 w-6 bg-slate-900 rounded-full"
                />
                <motion.span
                  animate={mobileMenuOpen ? { rotate: -45, y: 0 } : { rotate: 0, y: 6 }}
                  className="absolute block h-0.5 w-6 bg-slate-900 rounded-full"
                />
              </div>
            </button>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:gap-x-4 xl:gap-x-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors hover:text-[#876a57] relative group whitespace-nowrap ${
                  isActive(item.href) ? 'text-[#876a57]' : 'text-slate-900'
                }`}
              >
                {item.name}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#876a57] transition-all duration-300 ${isActive(item.href) ? 'w-full' : 'w-0 group-hover:w-full'}`} />
              </Link>
            ))}
          </div>
          
          {/* Right spacer to keep nav centered */}
          <div className="hidden lg:flex lg:flex-1 lg:justify-end" />
        </div>
      </nav>

      {/* Mobile menu Overlay and Panel */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              variants={overlayVariants}
              initial="closed"
              animate="opened"
              exit="closed"
              onClick={() => setMobileMenuOpen(false)}
              className="lg:hidden fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-sm"
            />

            {/* Side Panel */}
            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="opened"
              exit="closed"
              className="lg:hidden fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white shadow-2xl flex flex-col"
            >
              <div className="px-6 py-6 h-20 flex items-center border-b border-slate-50">
                <Link to="/" className="-m-1.5 p-1.5 flex items-center" onClick={() => setMobileMenuOpen(false)}>
                  <img src="/logo.png" alt="FINANJU" className="h-10 w-auto" />
                </Link>
              </div>

              <div className="flex-1 overflow-y-auto px-6 py-8">
                <nav className="flex flex-col gap-y-4">
                  {navigation.map((item) => (
                    <motion.div key={item.name} variants={itemVariants}>
                      <Link
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block text-2xl font-semibold tracking-tight transition-colors ${
                          isActive(item.href) ? 'text-[#876a57]' : 'text-slate-900'
                        }`}
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  ))}
                </nav>

                <motion.div 
                  variants={itemVariants}
                  className="mt-12"
                >
                  <div className="bg-[#876a57]/5 rounded-2xl p-6">
                    <p className="text-sm font-semibold text-[#876a57] mb-2 uppercase tracking-wider">Har du spørgsmål?</p>
                    <p className="text-slate-600 text-sm mb-4">Kontakt os i dag for en uforpligtende samtale om jeres behov.</p>
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center justify-center w-full rounded-full bg-[#876a57] px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#6d5546] transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Kontakt os
                    </Link>
                  </div>
                </motion.div>
              </div>

              <div className="px-6 py-6 border-t border-slate-50 text-xs text-slate-400">
                &copy; {new Date().getFullYear()} FINANJU ApS. Alle rettigheder forbeholdes.
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
